<template>
    <div
        :class="{ 'slds-has-error': isInvalid }"
    >
        <label
            v-if="showLabel"
            class="slds-form-element__label"
        >
            <abbr
                v-if="isRequired"
                :title="requiredLabel"
                class="slds-required"
            >*</abbr>
            {{ decodeHtml(label) }}
        </label>

        <div class="slds-form-element__control">
            <div class="slds-combobox_container">
                <div
                    :class="{ 'slds-is-open': showOptions, 'slds-combobox-picklist': !showOptions }"
                    class="slds-combobox slds-dropdown-trigger slds-dropdown-trigger_click"
                    role="combobox"
                >
                    <!-- COMBO BOX -->
                    <div
                        v-if="useCombobox"
                        ref="combobox"
                        class="slds-combobox__form-element slds-input-has-icon slds-input-has-icon_right"
                    >
                        <MAIconInline
                            :svg="require('!html-loader!@salesforce-ux/design-system/assets/icons/utility/clock.svg')"
                            :iconClass="['slds-icon', 'slds-icon_x-small', 'slds-icon-text-default']"
                            class="slds-icon_container slds-icon-utility-down slds-input__icon slds-input__icon_right"
                        />
                        <input
                            ref="select"
                            v-model="comboValue"
                            :class="{'slds-input-invalid': isInvalid}"
                            :placeholder="placeholder"
                            type="text"
                            class="slds-input slds-combobox__input"
                            @click="doShow"
                            @blur="applyValue"
                            @keyup.enter.prevent="enter"
                            @keyup.esc="reset"
                            @keydown.down.prevent="down"
                            @keydown.up.prevent="up"
                        >
                    </div>

                    <!-- REGULAR PICKLIST -->
                    <div
                        v-else
                        class="slds-combobox__form-element slds-input-has-icon slds-input-has-icon_right"
                        role="none"
                    >
                        <input
                            ref="select"
                            :disabled="isDisabled"
                            :value="input"
                            :placeholder="placeholder"
                            autocomplete="off"
                            type="text"
                            class="slds-input slds-combobox__input"
                            readonly=""
                            @click="doShow($event)"
                        >
                        <MAIconInline
                            :svg="require('!html-loader!@salesforce-ux/design-system/assets/icons/utility/down.svg')"
                            :iconClass="['slds-icon', 'slds-icon_x-small', 'slds-icon-text-default']"
                            class="slds-icon_container slds-icon-utility-down slds-input__icon slds-input__icon_right"
                        />
                    </div>

                    <!-- MULTI SELECT OPTIONS -->
                    <div
                        v-if="showPills"
                        role="listbox"
                        aria-orientation="horizontal"
                    >
                        <ul
                            class="slds-listbox slds-listbox_horizontal slds-p-top_xxx-small"
                            role="group"
                            style="overflow:hidden;"
                        >
                            <li
                                v-for="(option, index) in selectedOptions"
                                :key="index"
                                class="slds-listbox__item"
                            >
                                <span
                                    class="slds-pill"
                                    role="option"
                                >
                                    <span class="slds-pill__label">{{ getValue(option) }}</span>
                                    <span
                                        class="slds-icon_container slds-pill__remove"
                                        @click="removePill(option)"
                                    >
                                        <MAIconInline
                                            :svg="require('!html-loader!@salesforce-ux/design-system/assets/icons/utility/close.svg')"
                                            :iconClass="['slds-icon', 'slds-icon_x-small', 'slds-icon-text-default']"
                                            assistiveText="Press delete or backspace to remove"
                                        />
                                    </span>
                                </span>
                            </li>
                        </ul>
                    </div>

                    <!-- OPTIONS -->
                    <div
                        v-if="showOptions"
                        ref="dropdown"
                        v-detach-element="{ disabled: !doDetach, anchorRef: $refs['select'], viewPortOverflow: true, onRender, resizeToAnchor: true, scrollHandler: reset }"
                        v-click-outside:[clickOutsideOptions]
                        class="ma-select-dropdown"
                        role="listbox"
                    >
                        <!-- Option Filtering/Search -->
                        <div
                            v-if="!useCombobox && showOptionSearch"
                            class="search"
                        >
                            <div class="slds-form-element__control picklist-search-wrap slds-input-has-icon slds-input-has-icon_left-right">
                                <MAIconInline
                                    :svg="require('!html-loader!@salesforce-ux/design-system/assets/icons/utility/search.svg')"
                                    :iconClass="['slds-icon', 'slds-input__icon', 'slds-input__icon_left', 'slds-icon-text-default']"
                                />
                                <input
                                    ref="search"
                                    v-model="searchValue"
                                    type="text"
                                    class="slds-input picklist-search-input"
                                    @input="doSearch"
                                    @keyup.enter.prevent="enter"
                                    @keyup.esc="reset"
                                    @keydown.down.prevent="down"
                                    @keydown.up.prevent="up"
                                >
                                <button
                                    class="slds-input__icon slds-input__icon_right slds-button slds-button_icon"
                                    @click="clearSearch"
                                >
                                    <MAIconInline
                                        :svg="require('!html-loader!@salesforce-ux/design-system/assets/icons/utility/clear.svg')"
                                        :iconClass="['slds-button__icon', 'slds-icon-text-light']"
                                        :assistiveText="clearLabel"
                                    />
                                </button>
                            </div>
                        </div>

                        <!-- Results -->
                        <ul
                            ref="resultsWrapper"
                            class="slds-listbox slds-listbox_vertical slds-dropdown slds-dropdown_fluid"
                            role="presentation"
                        >
                            <!-- no-search-results option -->
                            <li
                                v-if="!results.length"
                                class="slds-listbox__item"
                                disabled="disabled"
                                role="presentation"
                            >
                                <div
                                    role="option"
                                    class="slds-media slds-listbox__option slds-listbox__option_plain slds-media_small slds-media_center"
                                >
                                    <span class="slds-media__body">
                                        <span class="slds-truncate">{{ noSearchResultsLabel }}</span>
                                    </span>
                                </div>
                            </li>

                            <!-- none option -->
                            <li
                                v-if="hasNoneOption && results.length"
                                class="slds-listbox__item"
                                role="presentation"
                                @mouseover="optionIndex = -1"
                                @mousedown.prevent="remove"
                            >
                                <div
                                    role="option"
                                    class="slds-media slds-listbox__option slds-listbox__option_plain slds-media_small slds-media_center"
                                >
                                    <span class="slds-media__figure">
                                        <MAIconInline
                                            :svg="require('!html-loader!@salesforce-ux/design-system/assets/icons/utility/check.svg')"
                                            :iconClass="['slds-icon', 'slds-icon_x-small', 'slds-listbox__icon-selected']"
                                        />
                                    </span>
                                    <span class="slds-media__body">
                                        <span class="slds-truncate">{{ noneLabel }}</span>
                                    </span>
                                </div>
                            </li>

                            <!-- options -->
                            <li
                                v-for="(result, index) in results"
                                :ref="'li' + index"
                                :key="index"
                                :class="{ 'slds-listbox__item': !result.isHeader, 'slds-text-title_caps slds-dropdown__header slds-truncate': result.isHeader }"
                                role="presentation"
                                @mouseover="optionIndex = index"
                                @mousedown.prevent="select(result, $event)"
                            >
                                <slot
                                    :result="result"
                                    :index="index"
                                    name="result"
                                >
                                    <span v-if="result.isHeader">
                                        {{ decodeHtml(result.header) }}
                                    </span>
                                    <div
                                        v-else
                                        :class="{ 'slds-has-focus': index === optionIndex, 'slds-is-selected': validateIsActive(result) === true }"
                                        role="option"
                                        class="slds-media slds-listbox__option slds-listbox__option_plain slds-media_small slds-media_center"
                                    >
                                        <span class="slds-media__figure">
                                            <MAIconInline
                                                :svg="require('!html-loader!@salesforce-ux/design-system/assets/icons/utility/check.svg')"
                                                :iconClass="['slds-icon', 'slds-icon_x-small', 'slds-listbox__icon-selected']"
                                            />
                                        </span>
                                        <span class="slds-media__body">
                                            <span
                                                class="slds-truncate"
                                                :title="decodeHtml(getValue(result))"
                                            >
                                                {{ decodeHtml(getValue(result)) }}
                                            </span>
                                        </span>
                                    </div>
                                </slot>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>

        <div
            v-if="isInvalid"
            class="slds-form-element__help"
        >
            <slot name="errors">This field is required.</slot>
        </div>
    </div>
</template>

<script>
    import decodeHtml from '../mixins/decode-html';
    import clickOutside from '../directives/click-outside';
    import detach from '../directives/detach';
    import MAIconInline from './ma-icon-inline';

    const {
        parseInt,
        getComputedStyle
    } = window;

    export default {
        name: 'MAPicklist',
        components: { MAIconInline },
        directives: {
            'click-outside': clickOutside,
            'detach-element': detach
        },
        mixins: [decodeHtml],
        props: {
            label: {
                type: String,
                default: ''
            },
            options: {
                type: Array,
                required: true
            },
            keyString: {
                type: String,
                default: 'key'
            },
            valueString: {
                type: String,
                default: 'value'
            },
            heightInNumItems: {
                type: Number,
                default: 5
            },
            hasNoneOption: {
                type: Boolean,
                default: true
            },
            useCombobox: {
                type: Boolean,
                default: false
            },
            showOptionSearch: {
                type: Boolean,
                default: true
            },
            value: {
                type: [String, Number],
                default: ''
            },
            default: {
                type: [String, Number],
                default: ''
            },
            isDisabled: {
                type: Boolean,
                default: false
            },
            isRequired: {
                type: Boolean,
                default: false
            },
            isInvalid: {
                type: Boolean,
                default: false
            },
            placeholder: {
                type: String,
                default: 'Select an Option'
            },
            doDetach: {
                type: Boolean,
                default: false
            },
            isMultiSelect: {
                type: Boolean,
                default: false
            },
            requiredLabel: {
                type: String,
                default: 'Required'
            },
            clearLabel: {
                type: String,
                default: 'Clear'
            },
            noSearchResultsLabel: {
                type: String,
                default: 'No search results.'
            },
            noneLabel: {
                type: String,
                default: '--None--'
            }
        },
        data() {
            return {
                showOptions: false,
                el: null,
                optionIndex: -1,
                results: [],
                defaultValue: this.default,
                input: this.getSelectedValue(),
                comboValue: this.getSelectedValue(),
                searchValue: '',
                selectedOptions: []
            };
        },
        computed: {
            clickOutsideOptions() {
                return {
                    active: this.showOptions && !this.useCombobox,
                    handler: this.reset
                };
            },
            showSelected() {
                let toCheck = this.value;

                if (typeof toCheck === 'number') {
                    toCheck = toCheck.toString();
                }

                return toCheck;
            },
            showPills() {
                const hasOptions = this.selectedOptions.length > 0;
                return (this.isMultiSelect && hasOptions);
            },
            showLabel() {
                return this.label;
            },
            multiSelectShortHand() {
                const selectionArr = this.selectedOptions;

                let localValue = '';
                if (selectionArr.length > 1) {
                    localValue = `${selectionArr.length} Options Selected`;
                } else if (selectionArr.length === 1) {
                    const [selectedValue] = selectionArr;
                    localValue = this.getValue(selectedValue);
                }

                return localValue;
            },
            resultsLength() {
                return this.results.length;
            }
        },
        watch: {
            value(val) {
                if (this.isMultiSelect) {
                    // update combovalue to list of keys to be returned on @input
                    this.selectedOptions = val;
                    this.comboValue = this.getSelectedValueMultiSelect() || [];
                } else {
                    this.input = this.getSelectedValue();
                    this.comboValue = this.input;
                }
            },
            options() {
                this.input = this.getSelectedValue();
            },
            selectedOptions() {
                // watch the input value and update to shorthand if multiple selections
                this.input = this.multiSelectShortHand;
            },
            resultsLength() {
                this.setDropdownHeight();
            }
        },
        methods: {
            // Custom onRender function we pass to the detached directive
            onRender() {
                this.setDropdownHeight();
                if (this.$refs.search) {
                    this.$refs.search.focus();
                }
            },
            validateIsActive(result) {
                let isActive = false;

                if (this.isMultiSelect) {
                    // compare selected Array with options
                    const localSelections = this.selectedOptions;
                    const foundMatch = localSelections.filter(obj => this.getKey(result) === this.getKey(obj));
                    isActive = foundMatch.length > 0;
                } else {
                    isActive = this.getKey(result) === this.value;
                }

                return isActive;
            },
            removePill(optionToRemove) {
                // remove our pill from selectedOptions
                const newOptions = [];
                // using for loop for speed
                for (let i = 0; i < this.selectedOptions.length; i++) {
                    const option = this.selectedOptions[i];
                    if (option.id !== optionToRemove.id) {
                        newOptions.push(option);
                    }
                }

                this.selectedOptions = newOptions;
                this.comboValue = this.getSelectedValueMultiSelect() || [];
                // on removal update parent
                this.$nextTick(() => {
                    this.$emit('input', this.comboValue);
                    this.$emit('change', this.selectedOptions);
                });
            },
            clearSearch() {
                this.searchValue = '';
                this.doSearch();
                this.$refs.search.focus();
            },
            getKey(option) {
                if (typeof option === 'string' || typeof option === 'number') return option;
                return option[this.keyString];
            },
            getValue(option) {
                if (typeof option === 'string' || typeof option === 'number') return option;
                return option[this.valueString];
            },
            getSelectedValue() {
                if (!this.options || !this.options.length) return '';

                let selectedValue;

                for (let i = 0; i < this.options.length; i++) {
                    const option = this.options[i];

                    if (typeof option === 'object' && option.children && Array.isArray(option.children)) {
                        selectedValue = option.children.find(_option => this.getKey(_option) === this.value);
                    } else if (this.getKey(option) === this.value) {
                        selectedValue = option;
                    }

                    if (selectedValue) break;
                }

                let result = (selectedValue) ? this.decodeHtml(this.getValue(selectedValue)) : '';
                if (!result && this.useCombobox) result = this.value;

                return result;
            },
            getSelectedValueMultiSelect() {
                if (!this.selectedOptions || !this.selectedOptions.length) return '';

                const selectedValues = [];
                for (let i = 0; i < this.selectedOptions.length; i++) {
                    const option = this.selectedOptions[i];
                    selectedValues.push(this.getValue(option));
                }
                // return array of options
                return selectedValues;
            },
            checkSelectedForDuplicates(selectionArr) {
                // using for loop for speed (large array becomes sluggish on filter)
                const seen = {};
                const out = [];
                const len = selectionArr.length;
                let j = 0;
                for (let i = 0; i < len; i++) {
                    const item = selectionArr[i];
                    if (seen[item[this.keyString]] !== 1) {
                        seen[item[this.keyString]] = 1;
                        out[j++] = item;
                    }
                }
                return out;
            },
            applyValue() {
                if (this.comboValue === '' || this.comboValue !== this.input) {
                    this.$emit('input', this.comboValue);
                }

                this.reset();
            },
            select(option) {
                if (option.isHeader) return;
                if (this.isMultiSelect) {
                    this.selectedOptions.push(option);
                    this.selectedOptions = this.checkSelectedForDuplicates(this.selectedOptions);
                }

                this.$nextTick(() => {
                    if (this.isMultiSelect) {
                        this.comboValue = this.getSelectedValueMultiSelect() || [];
                        this.$emit('input', this.comboValue);
                        this.$emit('change', this.selectedOptions);
                    } else {
                        this.$emit('input', this.getKey(option));
                        this.$emit('change', option);
                    }

                    this.reset();
                });
            },
            down() {
                if (this.optionIndex + 1 < this.results.length) this.optionIndex++;

                const dropdown = this.$refs.resultsWrapper;
                const paddingBuffer = parseInt(getComputedStyle(dropdown).paddingTop);

                let headerHeightBuffer = 0;

                // If next result option is a header then increment and get buffer height
                if (typeof this.results[this.optionIndex] === 'object' && this.results[this.optionIndex].isHeader) {
                    const headerEl = this.$refs[`li${this.optionIndex}`][0];
                    headerHeightBuffer = headerEl.clientHeight + paddingBuffer;
                    this.optionIndex++;
                }

                const li = this.$refs[`li${this.optionIndex}`][0];
                if ((li.offsetTop + li.clientHeight) - dropdown.scrollTop > dropdown.clientHeight) {
                    dropdown.scrollTop += li.clientHeight + headerHeightBuffer;
                }
            },
            up() {
                if (this.optionIndex - 1 < 0) return;

                this.optionIndex--;

                // If next result option is a header then increment
                if (typeof this.results[this.optionIndex] === 'object' && this.results[this.optionIndex].isHeader) this.optionIndex--;

                const dropdown = this.$refs.resultsWrapper;
                const li = this.$refs[`li${this.optionIndex}`][0];
                const listItemOffsetTop = li.offsetTop;

                if (listItemOffsetTop <= dropdown.scrollTop + dropdown.clientHeight && listItemOffsetTop >= dropdown.scrollTop) return;

                if (this.optionIndex === 0) {
                    dropdown.scrollTop = 0;
                } else if (listItemOffsetTop - li.clientHeight < listItemOffsetTop) {
                    dropdown.scrollTop = listItemOffsetTop - 4; // 5px buffer - aesthetic only
                }
            },
            enter() {
                if (this.results.length === 1) this.optionIndex = 0;
                if (!this.results.length || this.optionIndex < 0) return;

                this.select(this.results[this.optionIndex]);
            },
            doShow(e) {
                // TODO
                // Add check to see if options is an array or object and if it is an object check for an empty object
                // Object.getOwnPropertyNames(this.options).length
                if (!this.options.length || (e && e.target.className === 'remove')) return;
                this.showOptions = true;
                this.doSearch();
                this.setDropdownHeight();
            },
            setDropdownHeight() {
                this.$nextTick(() => {
                    const dropdown = this.$refs.resultsWrapper;
                    if (!dropdown) return;

                    const li = dropdown.getElementsByTagName('li')[0];
                    let numItems = (this.results.length > this.heightInNumItems) ? this.heightInNumItems : this.results.length;
                    if (this.hasNoneOption) numItems++;

                    this.dropdownHeight = numItems * li.clientHeight;
                    this.dropdownHeight += parseInt(getComputedStyle(li.parentNode).paddingTop);
                    this.dropdownHeight += parseInt(getComputedStyle(li.parentNode).paddingBottom);
                    this.dropdownHeight += parseInt(getComputedStyle(li.parentNode)['border-top-width']);
                    this.dropdownHeight += parseInt(getComputedStyle(li.parentNode)['border-bottom-width']);
                    dropdown.style.height = `${this.dropdownHeight}px`;
                });
            },
            doSearch() {
                const sanitizedValue = this.searchValue.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
                const regex = new RegExp(sanitizedValue, 'i');

                let results = [];

                this.options.forEach((option) => {
                    if (typeof option === 'object' && option.children && option.children.length) {
                        const filteredChildren = option.children.filter(child => regex.test(this.getValue(child)));

                        if (filteredChildren.length) {
                            if (option.header) {
                                results.push({
                                    isHeader: true,
                                    header: option.header
                                });
                            }

                            results = results.concat(filteredChildren);
                        }
                    } else if (regex.test(this.getValue(option))) {
                        results.push(option);
                    }
                });

                this.results = results;
            },
            remove() {
                this.$emit('input', null);
                this.reset();
            },
            reset() {
                this.optionIndex = -1;
                this.showOptions = false;
                this.searchValue = '';
            }
        }
    };
</script>

<style lang="scss" scoped>
    .slds-dropdown__header {
        margin-top: 4px;
    }

    .ma-select-dropdown {
        z-index: 99999;
        position: absolute;
        width: 100%;
    }

    ul {
        overflow-y: scroll;

        li.slds-listbox__item {

            .slds-listbox__option.slds-has-focus {
                background-color: #f3f2f2;
                text-decoration: none;
            }

            .slds-media__figure {
                font-size: 16px;
                color: #0070d2;
            }
        }
    }

    input[readonly] {
        cursor: pointer;
    }

    .slds-scope .slds-input-has-icon .slds-input__icon.slds-icon-utility-down {
        pointer-events: none; //allow click to go through the icon
    }

    .slds-scope .slds-combobox__form-element.slds-input-has-icon.slds-input-has-icon_right .slds-input__icon {
        font-size: 16px;
        color: #706e6b;
    }

    .slds-scope .slds-form-element .picklist-search-wrap {
        .slds-icon, .slds-icon::before, .slds-input__icon, .slds-input__icon::before {
            font-size: 14px;
            color: #b0adab;
        }

        button.slds-button_icon:hover {
            color: #005fb2;
        }
    }

    .slds-scope .slds-form-element.slds-has-error .picklist-search-wrap {
        .slds-input.picklist-search-input {
            background-color: #fff;
            border: 1px solid #dddbda;
            box-shadow: none;

            &:focus {
                box-shadow: 0 0 3px #0070D2;
                outline: 0;
                border-color: #1589ee;
            }
        }

        .slds-input__icon {
            color: #b0adab;
        }
    }

    .slds-scope .slds-has-error .slds-combobox__input {
        border-color: #c23934;
    }
</style>
